import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { sliderData, placesSliderData } from "data/uiData/RanksData";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function RankUnlockedModal({
  isOpen = false,
  setIsOpen,
  recent_milestone_achieved = {},
  placesAchived = {},
}) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [unlockedItems, setUnlockedItems] = useState([]);
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const {t} = useTranslation();

  useEffect(() => {
    if (isOpen) {
      const unlocked = [];

      // Safely process rank achievements
      if (
        recent_milestone_achieved &&
        typeof recent_milestone_achieved === "object"
      ) {
        Object.entries(recent_milestone_achieved).forEach(
          ([itemName, data]) => {
            if (data && data.status === true) {
              const rankItem = sliderData.find(
                (rank) => rank.title === itemName
              );
              if (rankItem) {
                unlocked.push({
                  ...rankItem,
                  type: "rank",
                  unlockDate: data.unlockDate,
                });
              }
            }
          }
        );
      }

      // Safely process place achievements
      if (placesAchived && typeof placesAchived === "object") {
        Object.entries(placesAchived).forEach(([itemName, data]) => {
          if (data && data.status === true) {
            const placeItem = placesSliderData.find(
              (place) => place.title === itemName
            );
            if (placeItem) {
              unlocked.push({
                ...placeItem,
                type: "place",
                unlockDate: data.unlockDate,
              });
            }
          }
        });
      }

      setUnlockedItems(unlocked);

      if (unlocked.length > 0) {
        setShowConfetti(true);
        const timer = setTimeout(() => setShowConfetti(false), 3000);
        return () => clearTimeout(timer);
      } else {
        // If no items to show, close the modal
        setIsOpen(false);
      }
    }
  }, [isOpen, recent_milestone_achieved, placesAchived, setIsOpen]);

  const confettiPieces = Array.from({ length: 50 }, (_, i) => i);

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "Unknown date";
    }
  };

  // Close modal if no unlocked items
  if (!isOpen || unlockedItems.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {confettiPieces.map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 rounded-sm"
                    style={{
                      backgroundColor: [
                        "#FFD700",
                        "#FF6B6B",
                        "#4ECDC4",
                        "#45B7D1",
                        "#96CEB4",
                        "#FFEAA7",
                      ][i % 6],
                      left: `${Math.random() * 100}%`,
                      top: "-10px",
                    }}
                    initial={{ y: -100, rotation: 0, opacity: 1 }}
                    animate={{
                      y: window.innerHeight + 100,
                      rotation: Math.random() * 360,
                      opacity: 0,
                    }}
                    transition={{
                      duration: Math.random() * 3 + 2,
                      delay: Math.random() * 2,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>
            )}

            <motion.div
              className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden flex flex-col"
              initial={{ scale: 0.3, opacity: 0, y: 100, rotateX: -15 }}
              animate={{ scale: 1, opacity: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.3, opacity: 0, y: 100, rotateX: 15 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
                mass: 0.8,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors text-white text-[23px] z-20"
              >
                ×
              </button>

              {/* Swiper section */}
              <div className="flex-grow h-[400px]">
                <Swiper
                  ref={swiperRef}
                  modules={[Navigation, Pagination]}
                  spaceBetween={50}
                  slidesPerView={1}
                  navigation={{
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                  }}
                  pagination={{
                    el: ".swiper-pagination",
                    type: "bullets",
                    clickable: true,
                  }}
                  speed={800}
                  className="h-full"
                >
                  {unlockedItems.map((item, index) => (
                    <SwiperSlide key={index}>
                      <SlideContent item={item} formatDate={formatDate} />
                    </SwiperSlide>
                  ))}

                  <div className="swiper-button-prev !text-white !bg-black/30 !rounded-full !w-10 !h-10 after:!text-xl"></div>
                  <div className="swiper-button-next !text-white !bg-black/30 !rounded-full !w-10 !h-10 after:!text-xl"></div>

                  <div className="swiper-pagination !bottom-4"></div>
                </Swiper>
              </div>

              {/* Button section with white background */}
              <div className="bg-white p-4">
                <motion.button
                  onClick={() => navigate("/quiz-game/ranks")}
                  className="w-full px-6 py-3 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 font-semibold cursor-pointer"
                  // initial={{ opacity: 0, y: 20 }}
                  // animate={{ opacity: 1, y: 0 }}
                  // transition={{ delay: 1.7 }}
                  // whileHover={{ scale: 1.02 }}
                  // whileTap={{ scale: 0.98 }}
                >
                  {t("quizGame.View All Milestones")}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function SlideContent({ item, formatDate }) {
  const { t } = useTranslation();
  const getRankColor = (type) => {
    return type === "rank"
      ? "from-yellow-400 via-yellow-500 to-yellow-600"
      : "from-blue-400 via-blue-500 to-blue-600";
  };

  const getRankBadge = (type) => {
    return type === "rank" ? "🏆" : "🏝️";
  };
  

  return (
    <div className="relative h-full p-8 text-center flex flex-col justify-center">
      <motion.div
        className={`absolute inset-0 bg-white opacity-10`}
        // animate={{
        //   background:
        //     item.type === "rank"
        //       ? [
        //           "linear-gradient(45deg, #FFD700, #FFA500)",
        //           "linear-gradient(135deg, #FFA500, #FFD700)",
        //           "linear-gradient(225deg, #FFD700, #FFA500)",
        //           "linear-gradient(315deg, #FFA500, #FFD700)",
        //         ]
        //       : [
        //           "linear-gradient(45deg, #3B82F6, #1E40AF)",
        //           "linear-gradient(135deg, #1E40AF, #3B82F6)",
        //           "linear-gradient(225deg, #3B82F6, #1E40AF)",
        //           "linear-gradient(315deg, #1E40AF, #3B82F6)",
        //         ],
        // }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-2 h-2 ${
              item.type === "rank" ? "bg-yellow-400" : "bg-blue-400"
            } rounded-full opacity-60`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.6, 1, 0.6],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {item.RankImage && (
        <motion.div
          className="relative mx-auto mb-6"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        >
          <motion.div
            className={`w-24 h-24 bg-gradient-to-br ${getRankColor(
              item.type
            )} rounded-full flex items-center justify-center shadow-2xl overflow-hidden`}
            animate={{
              boxShadow:
                item.type === "rank"
                  ? [
                      "0 0 20px rgba(255, 215, 0, 0.5)",
                      "0 0 40px rgba(255, 215, 0, 0.8)",
                      "0 0 20px rgba(255, 215, 0, 0.5)",
                    ]
                  : [
                      "0 0 20px rgba(59, 130, 246, 0.5)",
                      "0 0 40px rgba(59, 130, 246, 0.8)",
                      "0 0 20px rgba(59, 130, 246, 0.5)",
                    ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <img
              src={item.RankImage}
              alt={item.title || "Achievement"}
              className="w-16 h-16 object-cover rounded-full"
            />
          </motion.div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.h2
          className={`text-3xl font-bold bg-gradient-to-r px-10 sm:px-0 ${
            item.type === "rank"
              ? "from-yellow-600 to-yellow-800"
              : "from-blue-600 to-blue-800"
          } bg-clip-text text-transparent mb-2`}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          {item.type === "rank" ? t("quizGame.Rank") : t("quizGame.Place")}{" "}
          {t("quizGame.Unlocked")}! {getRankBadge(item.type)}
        </motion.h2>

        <motion.p
          className="text-xl font-semibold text-gray-700 mb-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {/* {item.title || "New Achievement"} */}


          {item.type === "rank" ? t(`quizGame.AllRanks.${item?.title}.title`) : t(`quizGame.AllPlaces.${item?.title}.title`)}
        </motion.p>

        <motion.p
          className="text-sm text-gray-500 mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          {t("quizGame.Unlocked")} {t("quizGame.on")}{" "}
          {formatDate(item.unlockDate)}
        </motion.p>

        <motion.p
          className="text-sm text-gray-600 mb-6 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          {item.type === "rank" ? t(`quizGame.AllRanks.${item?.title}.description`) : t(`quizGame.AllPlaces.${item?.title}.description`)}
        </motion.p>
      </motion.div>
    </div>
  );
}
